import DeliveryOptions from "./DeliveryOptions";
import FormField from "./FormField";
import { useSiteContent } from "@/lib/siteContent";
import { formatBRL } from "@/lib/format";
import type { CheckoutForm, DeliveryMethodId, ShippingResult } from "@/types";
import type { CheckoutErrors } from "@/lib/commerce";

interface StepRecebimentoProps {
  form: CheckoutForm;
  errors: CheckoutErrors;
  shipping: ShippingResult;
  subtotal: number;
  onChange: (name: string, value: string | boolean) => void;
  onBus: (name: keyof CheckoutForm["bus"], value: string) => void;
  onMethod: (id: DeliveryMethodId) => void;
}

export default function StepRecebimento({
  form,
  errors,
  shipping,
  subtotal,
  onBus,
  onMethod,
}: StepRecebimentoProps) {
  const content = useSiteContent();
  const method = form.deliveryMethod
    ? content.delivery.find((item) => item.id === form.deliveryMethod)
    : undefined;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-heading text-2xl text-foreground-900">Forma de recebimento</h2>
        <p className="mt-1 text-sm text-foreground-600">
          Escolha como prefere receber os seus produtos. Você pode mudar quando quiser.
        </p>
      </div>

      <DeliveryOptions value={form.deliveryMethod} onChange={onMethod} error={errors.deliveryMethod} />

      {method?.requiresBus ? (
        <div className="flex flex-col gap-5 rounded-lg border border-background-300 bg-background-100 p-4">
          <p className="text-sm text-foreground-700">
            Envio disponível para cidades atendidas por linhas parceiras. A disponibilidade, o horário
            e o ponto de retirada serão confirmados no atendimento.
          </p>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-[2fr_1fr]">
            <FormField
              label="Cidade de destino"
              name="destinationCity"
              value={form.bus.destinationCity}
              onChange={(_, value) => onBus("destinationCity", value)}
              error={errors.destinationCity}
              placeholder="Cidade de destino"
            />
            <FormField
              label="Estado"
              name="destinationState"
              value={form.bus.destinationState}
              onChange={(_, value) => onBus("destinationState", value.toUpperCase().slice(0, 2))}
              error={errors.destinationState}
              placeholder="GO"
              maxLength={2}
            />
          </div>
          <FormField
            label="Rodoviária ou ponto de retirada"
            name="terminal"
            value={form.bus.terminal}
            onChange={(_, value) => onBus("terminal", value)}
            error={errors.terminal}
            placeholder="Ex.: Rodoviária Central"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              label="Nome de quem vai retirar"
              name="responsibleName"
              value={form.bus.responsibleName}
              onChange={(_, value) => onBus("responsibleName", value)}
              error={errors.responsibleName}
              placeholder="Nome do responsável"
            />
            <FormField
              label="Telefone do responsável"
              name="responsiblePhone"
              value={form.bus.responsiblePhone}
              onChange={(_, value) => onBus("responsiblePhone", value)}
              error={errors.responsiblePhone}
              placeholder="(62) 99999-9999"
              type="tel"
              inputMode="tel"
            />
          </div>
        </div>
      ) : null}

      {form.deliveryMethod ? (
        <div className="rounded-lg border border-background-300 bg-background-50 p-4 text-sm text-foreground-700">
          <div className="flex items-center justify-between">
            <span>Taxa desta modalidade</span>
            <span className="font-medium text-foreground-900">
              {shipping.fee === null
                ? "Taxa a confirmar"
                : shipping.freeApplied
                  ? "Grátis"
                  : formatBRL(shipping.fee)}
            </span>
          </div>
          {subtotal > 0 ? (
            <p className="mt-1 text-xs text-foreground-500">
              Prazo: {method?.deadline}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}